import { Annotation } from "@/types/annotation";

// Fields stamped by the server and not allowed to drive a client-side PATCH.
// Strip them on both sides of the equality check so server-stamped values
// flowing back through React state don't cause spurious re-PATCHes.
const _AUDIT_FIELDS = ["createdAt", "createdBy", "updatedAt", "updatedBy"];

// Stable JSON serialization: object keys must serialize in the same order
// regardless of insertion order, otherwise two semantically-equal annotations
// (one server-parsed, one client-constructed) would compare unequal and
// trigger a redundant PATCH.
function _stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    // This serializer is not a React component; returning text is intentional.
    // eslint-disable-next-line react-google-translate/no-return-text-nodes
    return `[${value.map(_stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    // This serializer is not a React component; returning text is intentional.
    // eslint-disable-next-line react-google-translate/no-return-text-nodes
    return `{${keys
      .map(
        (k) =>
          `${JSON.stringify(k)}:${_stableStringify(
            (value as Record<string, unknown>)[k],
          )}`,
      )
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

// Compare two annotations by their persistable fields, ignoring server-stamped
// audit metadata and using key-sorted serialization so object-key ordering
// can't trigger false-positive PATCHes.
function _annotationContentEquals(a: Annotation, b: Annotation): boolean {
  const strip = (ann: Annotation): Record<string, any> => {
    const rest: Record<string, any> = { ...(ann as Record<string, any>) };
    for (const field of _AUDIT_FIELDS) delete rest[field];
    return rest;
  };
  return _stableStringify(strip(a)) === _stableStringify(strip(b));
}

// Diff two annotation arrays by id. Returns the per-mark operations needed to
// turn `baseline` into `next`. Sent as one bulk-apply request so the server
// can update only changed marks (concurrent edits to different marks don't
// clobber each other).
export function diffAnnotations(
  baseline: Annotation[],
  next: Annotation[],
): {
  created: Annotation[];
  updated: Annotation[];
  deletedIds: string[];
} {
  const baselineById = new Map(baseline.map((a) => [a.id, a]));
  const nextById = new Map(next.map((a) => [a.id, a]));

  const created: Annotation[] = [];
  const updated: Annotation[] = [];
  for (const ann of next) {
    const prior = baselineById.get(ann.id);
    if (!prior) {
      created.push(ann);
    } else if (!_annotationContentEquals(prior, ann)) {
      updated.push(ann);
    }
  }
  const deletedIds: string[] = [];
  for (const ann of baseline) {
    if (!nextById.has(ann.id)) deletedIds.push(ann.id);
  }
  return { created, updated, deletedIds };
}
